/* ============================================================================
   collection-reorder.js — personalized product order INSIDE collection pages
   (the plan's "reorder products within collections" + "move already-purchased
   products lower").

   Order rule, applied to the page's <product-list> cards:
     1. the visitor's recommended products, in recommendation order —
        SESSION-blended state when the blender produced one this session
        (sessionStorage.AGB_SessionRecs), else the offline list
        (localStorage.recommendedProducts)
     2. everything else, in the collection's own merchandised order
     3. products the visitor already OWNS (the colour system's owned map —
        the variant they bought), demoted to the end

   RESPECTS EXPLICIT INTENT: never reorders when the shopper chose a sort
   (?sort_by=) or applied filters (?filter.*) — their choice outranks ours.
   Re-runs once if the recommendation state is re-applied after we ran (the
   blender's agb:recs-applied announcement), so both systems settle together.

   ISOLATION: own file, reads state only (AGB_SessionRecs, recommendedProducts,
   SE_colorPref.answer.owned), touches only card order inside <product-list>.
   Kill switch: window.AGB_COLLECTION_REORDER_OFF = true (or sessionStorage).
   Emits one CAE `collection_reorder` event per applied reorder (Tier A).
   ============================================================================ */
(function () {
    'use strict';

    try { if (window.AGB_COLLECTION_REORDER_OFF === true) return; } catch (e) {}
    try { if (sessionStorage.getItem('AGB_COLLECTION_REORDER_OFF') === '1') return; } catch (e) { return; }

    var m = (location.pathname || '').match(/^\/collections\/([a-z0-9-]+)\/?$/);
    if (!m) return;                                     // collection LISTING pages only
    var collectionHandle = m[1];

    // A shopper-chosen sort or filter outranks personalization entirely.
    // Checked at RUN time, not load time: Prestige facets/sort update the URL
    // via pushState and re-render the section WITHOUT a reload, so a late
    // rec re-apply must re-read the current URL before touching anything.
    function log() {
        try {
            if (window.AGB_consoleCat && window.AGB_consoleCat('interaction')) {
                console.log.apply(console, ['[collection-reorder]'].concat([].slice.call(arguments)));
            }
        } catch (e) {}
    }

    /* THE PANEL OWNS THIS (Dorian 2026-09-08: "all the rest where we are
       reordering the product should be bypassed"). recommendations.js already
       gates its own reordering on the scarcity config's `dynamicUI` switches;
       this file is the newer half of the same feature and answered only to its
       own kill switch, so turning reordering off in the panel left it running.
       Now one control governs every recommendation-driven reorder, and the
       colour switch (productCardColor) is deliberately NOT consulted - the
       colour personalisation stays on when reordering goes off.
       Unresolved reads as ON, which is the contract recommendations.js
       documents: the config arrives asynchronously and must never block. */
    function reorderTurnedOff() {
        try {
            var d = window.__AGB_dynamicUI;
            if (!d) return false;
            var f = d.features || d;
            return f.productReorder === false;
        } catch (e) { return false; }
    }
    function shopperChoseOrder() {
        var search = location.search || '';
        return /[?&]sort_by=/.test(search) || /[?&]filter\./.test(search) || /[?&]page=/.test(search);
    }

    function recList() {
        try {
            var sr = JSON.parse(sessionStorage.getItem('AGB_SessionRecs') || 'null');
            if (sr && sr.length) return { list: sr, src: 'session' };
        } catch (e) {}
        try {
            var off = JSON.parse(localStorage.getItem('recommendedProducts') || 'null');
            if (off && off.length) return { list: off, src: 'offline' };
        } catch (e) {}
        return { list: [], src: 'none' };
    }

    function ownedHandles() {
        try {
            var cp = JSON.parse(localStorage.getItem('SE_colorPref') || 'null');
            var owned = cp && cp.answer && cp.answer.owned;
            return owned && typeof owned === 'object' ? owned : {};
        } catch (e) { return {}; }
    }

    var _mutations = 0;   // DOM reorders applied (cap 2: initial + one late re-apply)
    var _runs = 0;         // total run() invocations (compute bound only)

    // NO LAYOUT MOVEMENT UNDER THE SHOPPER (the plan doc's explicit rule).
    // Reordering a 48-card grid is a full-viewport shift, so it is only
    // allowed while the page is still young AND the shopper has not started
    // reading it. A late recommendation answer then simply does not reorder -
    // the next navigation gets the personalized order instead.
    var T0 = Date.now();
    var MAX_AGE_MS = 6000;
    var _engaged = false;
    function markEngaged() { _engaged = true; }
    try {
        window.addEventListener('scroll', markEngaged, { once: true, passive: true });
        window.addEventListener('pointerdown', markEngaged, { once: true, passive: true });
        window.addEventListener('keydown', markEngaged, { once: true });
    } catch (e) {
        window.addEventListener('scroll', markEngaged);
        window.addEventListener('pointerdown', markEngaged);
    }
    function tooLateToMove() {
        if (_mutations > 0) return false;   // an initial reorder already set the order
        return _engaged || (Date.now() - T0) > MAX_AGE_MS;
    }

    // The movable unit is the card's ancestor that is a DIRECT child of
    // <product-list>: on collection pages every card is wrapped in a
    // <div class="product"> that IS the grid item - moving the bare card out
    // of it leaves empty grid cells behind and dumps unwrapped cards at the
    // end (a several-hundred-px blank band). On carousel-style markup the
    // card is itself the direct child, and the walk-up returns it unchanged.
    function unitFor(card, container) {
        var el = card;
        while (el && el.parentElement && el.parentElement !== container) el = el.parentElement;
        return (el && el.parentElement === container) ? el : null;
    }

    function run() {
        if (_mutations >= 2 || _runs >= 8) return;
        _runs++;
        if (shopperChoseOrder()) return;                 // their choice outranks ours
        if (reorderTurnedOff()) { log('panel switch off - leaving the collection alone'); return; }
        if (tooLateToMove()) { log && log('past the no-shift window - skipping'); return; }
        var container = document.querySelector('product-list.product-list');
        if (!container) return;
        var cards = [].slice.call(container.querySelectorAll('product-card[handle]'));
        if (cards.length < 3) return;

        var rec = recList();
        if (!rec.list.length) return;
        var rank = {};
        for (var i = 0; i < rec.list.length; i++) {
            var h = rec.list[i] && rec.list[i].productLink;
            if (h && !(h in rank)) rank[h] = i;
        }
        var owned = ownedHandles();

        var seen = [];
        var keyed = [];
        for (var c = 0; c < cards.length; c++) {
            var unit = unitFor(cards[c], container);
            if (!unit || seen.indexOf(unit) !== -1) continue;   // one unit per wrapper
            seen.push(unit);
            var h2 = cards[c].getAttribute('handle') || '';
            var idx = keyed.length;
            var key;
            if (owned[h2]) key = 2000000 + idx;          // owned: demoted, stable
            else if (h2 in rank) key = rank[h2];         // recommended: rec order
            else key = 1000000 + idx;                    // rest: merchandised order
            keyed.push({ unit: unit, handle: h2, key: key, idx: idx });
        }
        if (keyed.length < 3) return;
        var before = keyed.map(function (k) { return k.handle; });
        keyed.sort(function (a, b) { return a.key - b.key || a.idx - b.idx; });
        var after = keyed.map(function (k) { return k.handle; });

        var moved = 0;
        for (var j = 0; j < before.length; j++) { if (before[j] !== after[j]) moved++; }
        if (!moved) return;                              // in order - keep the cap for a real change

        var frag = document.createDocumentFragment();
        keyed.forEach(function (k) { frag.appendChild(k.unit); });
        container.appendChild(frag);
        _mutations++;

        // PREVIEW-ONLY explainer toast through the engine's existing machinery
        // (master switch already preview-gated; role check = belt-and-braces).
        try {
            var role = window.Shopify && window.Shopify.theme && window.Shopify.theme.role;
            var eng = window.SE_Engine;
            if (role !== 'main' && eng && eng.debugToastsEnabled && typeof eng._emitInfoToast === 'function') {
                var demoted = Object.keys(owned).filter(function (h) {
                    return before.indexOf(h) !== -1;
                }).length;
                eng._emitInfoToast('collectionReorder', 'RECO \u00b7 COLLECTION',
                    'Collection "' + collectionHandle + '" personalized: ' + moved + ' of ' +
                    keyed.length + ' products moved (' + rec.src + ' recs first' +
                    (demoted ? ', ' + demoted + ' owned moved last' : '') + ')',
                    { source: 'reco', kind: 'info' });
            }
        } catch (e) {}

        try {
            if (window.CAEDev && typeof window.CAEDev.emit === 'function') {
                window.CAEDev.emit('collection_reorder', {
                    collection: collectionHandle,
                    src: rec.src,
                    moved: moved,
                    total: keyed.length,
                    owned_demoted: Object.keys(owned).filter(function (h) {
                        return before.indexOf(h) !== -1;
                    }).length
                });
            }
        } catch (e) {}
    }

    // RUN AT ONCE IF THE ANSWER IS ALREADY HERE.
    //
    // This file reads recommendations from STORAGE, never from the network -
    // recList() looks at sessionStorage.AGB_SessionRecs and then at
    // localStorage.recommendedProducts. For anybody who has been here before,
    // both are populated before the first card paints, so there was nothing to
    // wait for: the old boot waited 4s, or 500ms past the fetch's
    // agb:recs-applied, and the shopper watched the grid reshuffle about a
    // second after they could already read it (Dorian 2026-08-30).
    //
    // So: reorder on the first frame we have a grid AND a list. The wait is
    // kept only for the case it was written for - a visitor with nothing
    // stored yet, whose answer arrives from the network later.
    //
    // This makes the no-layout-shift rule STRONGER, not weaker. Moving cards
    // before the shopper can have read them is the whole point of that rule;
    // tooLateToMove() is unchanged and still refuses once they engage.
    function haveList() {
        var r = recList();
        return !!(r && r.list && r.list.length);
    }

    function bootNow() {
        if (haveList() && document.querySelector('product-list.product-list')) {
            log('recommendations already stored - ordering before first paint');
            run();
            return true;
        }
        return false;
    }

    // The grid can arrive after DOMContentLoaded (a section render, a facet
    // re-render). Watch briefly rather than guess a delay.
    function watchForGrid() {
        if (bootNow()) return;
        var give_up = Date.now() + 4000;
        var obs = new MutationObserver(function () {
            if (bootNow() || Date.now() > give_up) obs.disconnect();
        });
        try { obs.observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
        setTimeout(function () { obs.disconnect(); run(); }, 4000);   // the old fallback
    }

    // A fresh answer landing later re-runs once against it - our own DOM moves
    // dispatch no such event, so this cannot loop. No delay: by the time this
    // fires the state it reads has already been written.
    document.addEventListener('agb:recs-applied', function () { run(); });

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', watchForGrid);
    } else {
        watchForGrid();
    }
})();
